// People › Elections — per-election detail (#282).
// Party seat totals + a sortable per-division results table (winner, party,
// two-party-preferred %, swing, turnout). Winners ID-link to their
// parliamentarian profile via Phid.

import React, { useState, useEffect, Fragment } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Chip, Alert, Breadcrumbs, Link,
  Table, TableBody, TableRow, TableCell, TableHead, TableContainer,
  TableSortLabel, IconButton, Collapse,
} from '@mui/material';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getElection } from '../services/api';
import useTableSort from '../hooks/useTableSort';

const fmtPct = (v) => (v === null || v === undefined || v === '' ? '—' : `${v}%`);
const fmtSwing = (v) => (v === null || v === undefined || v === '' ? '—' : `${v > 0 ? '+' : ''}${v}`);

// One division row that expands to the full candidate field (first preferences).
function DivisionRow({ d }) {
  const [open, setOpen] = useState(false);
  const candidates = d.candidates || [];
  const hasCandidates = candidates.length > 0;
  return (
    <Fragment>
      <TableRow hover sx={{ '& > *': { borderBottom: hasCandidates && open ? 'unset' : undefined } }}>
        <TableCell sx={{ width: 40 }}>
          {hasCandidates && (
            <IconButton size="small" onClick={() => setOpen((o) => !o)}
              aria-label="show candidates">
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{d.Division}</TableCell>
        <TableCell>{d.State || '—'}</TableCell>
        <TableCell>
          {d.Phid ? (
            <Link component={RouterLink} to={`/parliamentarians/${d.Phid}`} underline="hover">
              {d.Winner}
            </Link>
          ) : (d.Winner || '—')}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {d.PartyColour && (
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: d.PartyColour, flexShrink: 0 }} />
            )}
            {d.Party || '—'}
          </Box>
        </TableCell>
        <TableCell>{fmtPct(d.TCP_pct)}</TableCell>
        <TableCell>{fmtSwing(d.Swing)}</TableCell>
        <TableCell>{fmtPct(d.Turnout)}</TableCell>
      </TableRow>
      {hasCandidates && (
        <TableRow>
          <TableCell sx={{ py: 0 }} colSpan={8}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ my: 1, ml: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Candidates — {d.Division} ({candidates.length}), by first-preference vote
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Candidate', 'Party', 'Votes', 'First-pref %', 'Swing', ''].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidates.map((c, i) => (
                      <TableRow key={`${c.Candidate}-${i}`}>
                        <TableCell>
                          {c.Phid ? (
                            <Link component={RouterLink} to={`/parliamentarians/${c.Phid}`} underline="hover">
                              {c.Candidate}
                            </Link>
                          ) : (c.Candidate || '—')}
                        </TableCell>
                        <TableCell>{c.Party || '—'}</TableCell>
                        <TableCell>{c.Votes != null ? c.Votes.toLocaleString() : '—'}</TableCell>
                        <TableCell>{fmtPct(c.Percentage)}</TableCell>
                        <TableCell>{fmtSwing(c.Swing)}</TableCell>
                        <TableCell>
                          {c.Status ? <Chip size="small" color="success" label={c.Status} /> : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU',
  { day: '2-digit', month: 'long', year: 'numeric' }) : '');

const SORT_KEYS = {
  Division: (d) => d.Division || '',
  State: (d) => d.State || '',
  Winner: (d) => d.Winner || '',
  Party: (d) => d.Party || '',
  'TCP %': (d) => (d.TCP_pct == null ? -1 : d.TCP_pct),
  Swing: (d) => (d.Swing == null ? -999 : d.Swing),
  'Turnout %': (d) => (d.Turnout == null ? -1 : d.Turnout),
};

export default function ElectionDetail() {
  const { eid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        setData(await getElection(eid));
      } catch (err) {
        setError(err.response?.status === 404 ? 'Election not found' : 'Failed to load election');
      } finally {
        setLoading(false);
      }
    })();
  }, [eid]);

  const divisions = data?.divisions || [];
  const { order, orderBy, sortedRows, handleSort } = useTableSort(
    divisions, (d, col) => SORT_KEYS[col](d));

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

  const title = data.is_by_election
    ? `${data.Division} by-election ${data.Year}`
    : `${data.Year} federal election`;
  const cols = ['Division', 'State', 'Winner', 'Party', 'TCP %', 'Swing', 'Turnout %'];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/elections" underline="hover" color="inherit">
          Elections
        </Link>
        <Typography color="text.primary">{title}</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <HowToVoteIcon color="primary" /> {title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {fmtDate(data.Date)} · {data.is_by_election ? 'By-election' : 'General election'}
        {data.Reps && data.Senate ? ' · House + half-Senate' : data.Senate ? ' · Senate' : ' · House'}
      </Typography>

      {/* Party seat totals */}
      {data.reps_counts?.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>House seats won</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {data.reps_counts.map((c) => (
              <Chip key={c.Party} label={`${c.Party} — ${c.Count}`}
                sx={{
                  bgcolor: c.PartyColour || undefined,
                  color: c.PartyColour ? '#fff' : undefined,
                  fontWeight: 500,
                }} />
            ))}
          </Box>
        </Paper>
      )}

      {/* Per-division results (general elections) */}
      {divisions.length > 0 && (
        <Paper elevation={1} sx={{ borderRadius: 3 }}>
          <Typography variant="h6" sx={{ p: 2, pb: 1 }}>
            Division results ({divisions.length})
          </Typography>
          <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  {cols.map((c) => (
                    <TableCell key={c} sx={{ fontWeight: 600 }}
                      sortDirection={orderBy === c ? order : false}>
                      <TableSortLabel active={orderBy === c}
                        direction={orderBy === c ? order : 'asc'}
                        onClick={() => handleSort(c)}>{c}</TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((d, i) => (
                  <DivisionRow key={`${d.Division}-${i}`} d={d} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {data.is_by_election && divisions.length === 0 && data.reps_counts?.length === 0 && (
        <Alert severity="info">
          Per-candidate results aren't available for this by-election in the source data.
        </Alert>
      )}
    </Box>
  );
}
